// ./src/components/features/Connectors/ConfigEditors/TrelloConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Text,
  Anchor,
  Alert,
  Code,
  Switch,
  Group,
  Box,
  List,
} from "@mantine/core";
import { IconInfoCircle, IconLayoutKanban } from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { TrelloConnectorConfig } from "@/types";

export interface TrelloConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: TrelloConnectorConfig = {
  apiKey: "",
  apiToken: "",
  listId: "",
  namePattern: "{{formName}}: New submission",
  includeMetadata: true,
};

/**
 * Parse and validate Trello connector configuration
 */
const parseTrelloConfig = (jsonString: string): TrelloConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  return parsed;
};

export const TrelloConnectorConfigEditor: React.FC<
  TrelloConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<TrelloConnectorConfig>(() =>
    parseTrelloConfig(value)
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseTrelloConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: TrelloConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle field changes
  const handleApiKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, apiKey: e.target.value });
  };

  const handleApiTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, apiToken: e.target.value });
  };

  const handleListIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, listId: e.target.value });
  };

  const handleNamePatternChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, namePattern: e.target.value });
  };

  const handleIncludeMetadataChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, includeMetadata: e.currentTarget.checked });
  };

  // Build authorization URL
  const authUrl = config.apiKey
    ? `https://trello.com/1/authorize?expiration=never&scope=read,write&response_type=token&name=FormSubmits&key=${config.apiKey}`
    : null;

  return (
    <Stack gap="md">
      {/* Setup Instructions */}
      <Alert icon={<IconLayoutKanban size={16} />} color="blue" variant="light">
        <Text size="sm" fw={500} mb="xs">
          Setup Instructions:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            Go to{" "}
            <Anchor
              href="https://trello.com/power-ups/admin"
              target="_blank"
              rel="noopener noreferrer"
            >
              trello.com/power-ups/admin
            </Anchor>
          </List.Item>
          <List.Item>Click on "API Key" section</List.Item>
          <List.Item>Copy your API Key</List.Item>
          <List.Item>
            Click "generate a Token" link (or use link below after entering API
            Key)
          </List.Item>
          <List.Item>Authorize and copy the token</List.Item>
        </List>
      </Alert>

      {/* API Key Field */}
      <TextInput
        label="API Key"
        placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
        description="32-character API key from Trello"
        value={config.apiKey}
        onChange={handleApiKeyChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Authorization URL helper */}
      {authUrl && (
        <Alert
          icon={<IconInfoCircle size={16} />}
          color="green"
          variant="light"
        >
          <Text size="sm">
            <Anchor href={authUrl} target="_blank" rel="noopener noreferrer">
              Click here to generate your API Token
            </Anchor>
          </Text>
        </Alert>
      )}

      {/* API Token Field */}
      <TextInput
        label="API Token"
        placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
        description="64-character token generated via authorization"
        value={config.apiToken}
        onChange={handleApiTokenChange}
        disabled={disabled}
        required
        size="md"
        type="password"
        autoComplete="off"
      />

      {/* List ID Field */}
      <TextInput
        label="List ID"
        placeholder="xxxxxxxxxxxxxxxxxxxxxxxx"
        description="ID of the list where cards will be created"
        value={config.listId}
        onChange={handleListIdChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Help for List ID */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          How to find List ID:
        </Text>
        <List size="xs" spacing={2}>
          <List.Item>Open your Trello board</List.Item>
          <List.Item>
            Add <Code>.json</Code> to the end of the URL
          </List.Item>
          <List.Item>
            Search for your list name and find its <Code>"id"</Code> value
          </List.Item>
        </List>
        <Text size="xs" mt={4}>
          Or: Open a card in the list → Share → Export JSON → find{" "}
          <Code>idList</Code>
        </Text>
      </Alert>

      {/* Card Name Pattern Field */}
      <TextInput
        label="Card Name Pattern"
        placeholder="{{formName}}: New submission"
        description="Customize the card title"
        value={config.namePattern || "{{formName}}: New submission"}
        onChange={handleNamePatternChange}
        disabled={disabled}
        size="md"
      />

      {/* Toggle Options */}
      <Box>
        <Group justify="space-between">
          <div>
            <Text size="sm">Include Metadata</Text>
            <Text size="xs" c="dimmed">
              Add timestamp, submission ID, and view link to card description
            </Text>
          </div>
          <Switch
            checked={config.includeMetadata !== false}
            onChange={handleIncludeMetadataChange}
            disabled={disabled}
          />
        </Group>
      </Box>

      {/* Placeholders Help */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          Available placeholders:
        </Text>
        <Text size="xs" component="div">
          <Code>{"{{formName}}"}</Code> — Form name
          <br />
          <Code>{"{{formId}}"}</Code> — Form UUID
          <br />
          <Code>{"{{requestId}}"}</Code> — Submission ID
          <br />
          <Code>{"{{date}}"}</Code> — Date (YYYY-MM-DD)
        </Text>
      </Alert>

      {/* How It Works */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm" fw={500} mb="xs">
          How it works:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            Each submission creates a new card at the top of the list
          </List.Item>
          <List.Item>
            Card description contains all form field data (Markdown formatted)
          </List.Item>
          <List.Item>Hidden and security fields are excluded</List.Item>
        </List>
      </Alert>
    </Stack>
  );
};
